import { pushDataLayerEvent } from './analytics'
import { hostOrigin } from './config'
import { captureContext, onNavigation } from './context'
import { parseMessage, postToIframe } from './messaging'
import { applyResize, styleFrame } from './resize'
import type {
  EmbedConfig,
  EventPayload,
  ResizePayload,
  ViewportPayload,
  VyroChatMessage,
} from './types'

const LOADED_FLAG = '__vyroChatEmbedLoaded'
const IFRAME_ID = 'vyro-chat-embed-frame'
/** Host viewport width below which the widget uses its mobile layout. */
const MOBILE_BREAKPOINT = 768

function readViewport(win: Window): ViewportPayload {
  const width = win.innerWidth
  const height = win.innerHeight
  return { width, height, isMobile: width > 0 && width < MOBILE_BREAKPOINT }
}

export interface LoaderHandle {
  iframe: HTMLIFrameElement
  destroy: () => void
}

interface LoadedWindow extends Window {
  [LOADED_FLAG]?: boolean
}

function buildIframeSrc(config: EmbedConfig): string {
  const query = `showroom=${encodeURIComponent(config.showroom)}`
  return `${config.host}/iframes/chat?${query}`
}

/**
 * Create the launcher iframe, wire the postMessage bridge, and start the
 * context feed. Idempotent — a second call (e.g. GTM double-injection) is a
 * no-op and returns null. Everything is guarded so a failure never breaks the
 * host page.
 */
export function initChatEmbed(
  win: Window,
  config: EmbedConfig,
): LoaderHandle | null {
  const target = win as LoadedWindow
  if (target[LOADED_FLAG]) return null
  target[LOADED_FLAG] = true

  const doc = win.document
  const origin = hostOrigin(config.host)

  const iframe = doc.createElement('iframe')
  iframe.id = IFRAME_ID
  iframe.src = buildIframeSrc(config)
  iframe.title = 'Vyro chat'
  iframe.setAttribute('loading', 'eager')
  iframe.setAttribute('allow', 'clipboard-write')
  iframe.setAttribute('allowtransparency', 'true')
  styleFrame(iframe, config.position)

  const sendContext = () => {
    postToIframe(iframe, origin, 'vyro:chat:context', captureContext(win))
  }

  const sendViewport = () => {
    postToIframe(iframe, origin, 'vyro:chat:viewport', readViewport(win))
  }

  const handleMessage = (message: VyroChatMessage) => {
    switch (message.code) {
      case 'vyro:chat:ready':
        // Hand the iframe both the page context and the true host viewport so
        // it can size the panel and pick its mobile/desktop layout correctly.
        sendContext()
        sendViewport()
        break
      case 'vyro:chat:resize':
        applyResize(iframe, message.payload as ResizePayload, readViewport(win))
        break
      case 'vyro:chat:event':
        pushDataLayerEvent(win, message.payload as EventPayload)
        break
      default:
        break
    }
  }

  const onMessage = (event: MessageEvent) => {
    const message = parseMessage(event, origin)
    if (message) handleMessage(message)
  }

  win.addEventListener('message', onMessage)
  // Re-feed the host viewport on resize so an open panel tracks the window.
  win.addEventListener('resize', sendViewport)
  const teardownNavigation = onNavigation(win, sendContext)

  const append = () => doc.body.appendChild(iframe)
  if (doc.body) {
    append()
  } else {
    doc.addEventListener('DOMContentLoaded', append, { once: true })
  }

  return {
    iframe,
    destroy: () => {
      win.removeEventListener('message', onMessage)
      win.removeEventListener('resize', sendViewport)
      teardownNavigation()
      iframe.parentNode?.removeChild(iframe)
      target[LOADED_FLAG] = false
    },
  }
}
